import { useEffect, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'
import { DEFAULT_FOLDER_ID } from '../../../../constants'
import { useIsFeatureEnabled } from '../../../../services/api/feature-flags.hooks'
import {
    useGetGoalRecurringTemplateLinks,
    useGetGoals,
    useLinkRecurringTemplateToGoals,
} from '../../../../services/api/goals.hooks'
import { useCreateRecurringTask, useModifyRecurringTask } from '../../../../services/api/recurring-tasks.hooks'
import { Border, Colors, Spacing } from '../../../../styles'
import { mediaQuery } from '../../../../styles/dimensions'
import { RecurrenceRate } from '../../../../utils/enums'
import { TRecurringTaskTemplate, TTaskV4 } from '../../../../utils/types'
import { stopKeydownPropogation } from '../../../../utils/utils'
import Flex from '../../../atoms/Flex'
import GTButton from '../../../atoms/buttons/GTButton'
import { BodyMedium } from '../../../atoms/typography/Typography'
import { GoalHookCheckboxList, toggleHookSelection } from '../../../goals/RecurringGoalHooks'
import { activeGoals } from '../../../goals/goalTypes'
import GTModal from '../../../mantine/GTModal'
import CreateNewItemInput from '../../CreateNewItemInput'
import { getInitialSelectedDate } from '../recurringTasks.utils'
import DatePicker from './DatePicker'
import RecurrenceRateSelector from './RecurrenceRateSelector'
import TemplateFolderSelector from './TemplateFolderSelector'

// The form and the date picker sit side by side on desktop and stack on a phone.
const SettingsRow = styled.div`
    display: flex;
    flex: 1;
    justify-content: space-between;
    ${mediaQuery.phone} {
        flex-direction: column;
    }
`
const SettingsForm = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;
    border-right: ${Border.stroke.medium} solid ${Colors.background.border};
    padding-right: ${Spacing._24};
    ${mediaQuery.phone} {
        border-right: none;
        border-bottom: ${Border.stroke.medium} solid ${Colors.background.border};
        padding-right: 0;
        padding-bottom: ${Spacing._16};
    }
`
const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: ${Spacing._24};
`

interface RecurringTaskTemplateModalProps {
    onClose: () => void
    initialTitle?: string // takes precedence over initialTask
    initialRecurringTaskTemplate?: TRecurringTaskTemplate // takes precedence over initial fields below
    initialTask?: TTaskV4
    initialFolderId?: string
    initialRecurrenceRate?: RecurrenceRate
    initialSelectedDate?: DateTime
}
const RecurringTaskTemplateModal = ({
    onClose,
    initialTask,
    initialRecurringTaskTemplate,
    initialTitle,
    initialFolderId,
    initialRecurrenceRate,
    initialSelectedDate,
}: RecurringTaskTemplateModalProps) => {
    const { mutate: modifyRecurringTask } = useModifyRecurringTask()
    const { mutate: createRecurringTask } = useCreateRecurringTask()
    const { mutate: linkRecurringTemplateToGoals } = useLinkRecurringTemplateToGoals()
    const { data: goals = [] } = useGetGoals()
    // A disabled query keeps its cached data, so check the flag too: after opting out without a
    // reload, the cached goals must not offer hooks whose save would hit a 404.
    const areGoalsEnabled = useIsFeatureEnabled('goals')
    const hookableGoals = areGoalsEnabled ? activeGoals(goals) : []
    const recurringTemplateLinksQuery = useGetGoalRecurringTemplateLinks()
    const recurringTemplateLinks = recurringTemplateLinksQuery.data ?? {}

    const [title, setTitle] = useState(initialTitle ?? initialRecurringTaskTemplate?.title ?? initialTask?.title ?? '')
    const [recurrenceRate, setRecurrenceRate] = useState(
        initialRecurringTaskTemplate?.recurrence_rate ?? initialRecurrenceRate ?? RecurrenceRate.WEEKLY
    )
    const [folder, setFolder] = useState(
        initialRecurringTaskTemplate?.id_task_section ?? initialFolderId ?? DEFAULT_FOLDER_ID
    )
    const [selectedDate, setSelectedDate] = useState<DateTime>(
        () => initialSelectedDate ?? getInitialSelectedDate(initialRecurringTaskTemplate)
    )
    const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([])
    const initializedGoalHooks = useRef(false)
    const isValid = !!title.trim()

    useEffect(() => {
        if (initializedGoalHooks.current || !initialRecurringTaskTemplate || !recurringTemplateLinksQuery.data) return
        initializedGoalHooks.current = true
        setSelectedGoalIds(recurringTemplateLinks[initialRecurringTaskTemplate.id] ?? [])
    }, [initialRecurringTaskTemplate, recurringTemplateLinks, recurringTemplateLinksQuery.data])

    const toggleGoal = (goalId: string) => setSelectedGoalIds((current) => toggleHookSelection(current, goalId))

    const handleSave = () => {
        if (!isValid) return
        let dayToCreateTask: number | undefined = undefined
        if (recurrenceRate === RecurrenceRate.WEEKLY) {
            dayToCreateTask = selectedDate.weekday
        } else if (recurrenceRate === RecurrenceRate.MONTHLY || recurrenceRate === RecurrenceRate.YEARLY) {
            dayToCreateTask = selectedDate.day
        }
        const payload = {
            title,
            recurrence_rate: recurrenceRate,
            id_task_section: folder,
            day_to_create_task: dayToCreateTask,
            month_to_create_task: recurrenceRate === RecurrenceRate.YEARLY ? selectedDate.month : undefined,
        }
        if (initialRecurringTaskTemplate) {
            // modifying a template
            modifyRecurringTask(
                {
                    id: initialRecurringTaskTemplate.id,
                    ...payload,
                },
                initialRecurringTaskTemplate.optimisticId
            )
            if (areGoalsEnabled && initializedGoalHooks.current) {
                linkRecurringTemplateToGoals(
                    {
                        id: initialRecurringTaskTemplate.id,
                        goalIds: selectedGoalIds,
                    },
                    initialRecurringTaskTemplate.optimisticId
                )
            }
        } else {
            const optimisticId = uuidv4()
            // creating a new template
            createRecurringTask({
                ...payload,
                optimisticId,
                time_of_day_seconds_to_create_task: 0,
                body: initialTask?.body,
                priority_normalized: initialTask?.priority_normalized,
                task_id: initialTask?.id,
            })
            if (areGoalsEnabled && selectedGoalIds.length > 0) {
                linkRecurringTemplateToGoals(
                    {
                        id: optimisticId,
                        goalIds: selectedGoalIds,
                    },
                    optimisticId
                )
            }
        }
        onClose()
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSave()
        }
        stopKeydownPropogation(e, undefined, true)
    }

    return (
        <GTModal
            open
            setIsModalOpen={onClose}
            size="sm"
            tabs={{
                title: `${initialRecurringTaskTemplate ? 'Edit' : 'Create'} a recurring task`,
                body: (
                    <>
                        <SettingsRow onKeyDown={handleKeyDown}>
                            <SettingsForm>
                                <CreateNewItemInput
                                    placeholder="Recurring task title"
                                    initialValue={title}
                                    autoFocus
                                    hideIcon
                                    onChange={setTitle}
                                    onSubmit={handleSave}
                                />
                                <Flex column gap={Spacing._12}>
                                    <BodyMedium>Which folder should this task appear in?</BodyMedium>
                                    <TemplateFolderSelector value={folder} onChange={setFolder} useTriggerWidth />
                                </Flex>
                                <RecurrenceRateSelector
                                    value={recurrenceRate}
                                    onChange={setRecurrenceRate}
                                    selectedDate={selectedDate}
                                />
                                {hookableGoals.length > 0 && (
                                    <Flex column gap={Spacing._12}>
                                        <BodyMedium>Goal hooks</BodyMedium>
                                        <GoalHookCheckboxList
                                            items={hookableGoals}
                                            selectedIds={selectedGoalIds}
                                            onToggle={toggleGoal}
                                            rowClassName="gap-2 rounded-none px-0 py-0 text-[inherit]"
                                            titleClassName="text-body-md"
                                        />
                                    </Flex>
                                )}
                            </SettingsForm>
                            <DatePicker date={selectedDate} setDate={setSelectedDate} recurrenceRate={recurrenceRate} />
                        </SettingsRow>
                        <Footer>
                            <GTButton styleType="primary" value="Save" onClick={handleSave} disabled={!isValid} />
                        </Footer>
                    </>
                ),
            }}
        />
    )
}

export default RecurringTaskTemplateModal
